'use client'

import { useId, useState } from 'react'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { GiftIcon, LockIcon, StarIcon } from 'lucide-react'

import { calculateAmountWithFee, MINIMUM_DONATION_AMOUNT } from '@/utilities/stripe'
import { cn } from '@/utilities/ui'

type PaymentAmountGridProps = {
  values: number[]
  suggestedDonation?: number
  amount: number | null
  onAmountChange: (amount: number | null) => void
  note: string
  onNoteChange: (note: string) => void
}

type AmountButtonProps = {
  radioGroupName: string
  value: string
  isSelected: boolean
  isSuggested?: boolean
  onChange: (value: string) => void
  children: React.ReactNode
}

// Format amount for display
const formatAmount = (amount: number) => {
  const formattedNumber = amount.toLocaleString('en-US', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  })
  return `$${formattedNumber}`
}

const AmountButton = ({
  radioGroupName,
  value,
  isSelected,
  isSuggested,
  onChange,
  children,
}: AmountButtonProps) => {
  return (
    <label
      className={cn(
        'flex w-full cursor-pointer items-center rounded-xl border border-border px-4 py-3 font-body text-base font-medium transition-all',
        'focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0 focus-visible:outline-none',
        'bg-card hover:bg-background',
        isSuggested && 'ring-2 ring-evergreen-200',
        isSuggested && !isSelected && 'bg-evergreen-50',
        isSelected && 'border-evergreen-600 bg-evergreen-800 text-white hover:bg-evergreen-700',
      )}
    >
      <input
        type="radio"
        name={radioGroupName}
        value={value}
        checked={isSelected}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
      {isSuggested ? (
        <StarIcon
          className="size-9 shrink-0 text-evergreen-600 transition-all"
          aria-label="Suggested donation amount"
        />
      ) : (
        <span className="size-9 shrink-0" aria-hidden />
      )}
      <span className="flex-1 text-center">{children}</span>
      <span className="size-9 shrink-0" aria-hidden />
    </label>
  )
}

type CustomAmountInputProps = {
  onAmountChange: (amount: number | null) => void
}

const CustomAmountInput = ({ onAmountChange }: CustomAmountInputProps) => {
  const [value, setValue] = useState('')

  const handleChange = (inputValue: string) => {
    setValue(inputValue)
    const numValue = parseFloat(inputValue)
    onAmountChange(!isNaN(numValue) && numValue > 0 ? numValue : null)
  }

  return (
    <div className="mt-4">
      <Input
        type="number"
        step="1"
        min="1"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Enter amount"
        className="w-full font-body"
      />
    </div>
  )
}

const PaymentAmountGrid = ({
  values,
  suggestedDonation,
  amount,
  onAmountChange,
  note,
  onNoteChange,
}: PaymentAmountGridProps) => {
  const [isCustomSelected, setIsCustomSelected] = useState(false)
  const radioGroupName = 'donation-amount'

  const selectedValue = amount !== null ? amount.toString() : 'custom'

  const handleRadioChange = (value: string) => {
    if (value === 'custom') {
      setIsCustomSelected(true)
      onAmountChange(null)
    } else {
      setIsCustomSelected(false)
      const numValue = parseFloat(value)
      onAmountChange(numValue)
    }
  }

  return (
    <div className="space-y-4">
      <div
        className="grid gap-2 xs:gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(max(150px, 33% - 1rem), 1fr))' }}
        role="radiogroup"
      >
        {values.map((value) => {
          const valueStr = value.toString()
          const isSelected = !isCustomSelected && selectedValue === valueStr
          const hasUserSelected = amount !== null || isCustomSelected
          const isSuggested =
            suggestedDonation != null && value === suggestedDonation && !hasUserSelected
          return (
            <AmountButton
              key={value}
              radioGroupName={radioGroupName}
              value={valueStr}
              isSelected={isSelected}
              isSuggested={isSuggested}
              onChange={handleRadioChange}
            >
              {formatAmount(value)}
            </AmountButton>
          )
        })}
        <AmountButton
          radioGroupName={radioGroupName}
          value="custom"
          isSelected={isCustomSelected}
          onChange={handleRadioChange}
        >
          Custom
        </AmountButton>
      </div>
      {isCustomSelected && <CustomAmountInput onAmountChange={onAmountChange} />}
      <div className="mt-4">
        <Textarea
          placeholder="Add an optional note for your donation"
          className="h-24 w-full font-body"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
        />
      </div>
    </div>
  )
}

type DonationSelectionScreenProps = {
  donationAmounts: number[]
  suggestedDonation?: number
  setClientSecret: (secret: string) => void
}

export const DonateSelectionScreen = ({
  donationAmounts,
  suggestedDonation,
  setClientSecret,
}: DonationSelectionScreenProps) => {
  const [amount, setAmount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isMonthly, setIsMonthly] = useState(false)
  const [coverProcessingFee, setCoverProcessingFee] = useState(false)
  const [note, setNote] = useState('')
  const monthlyCheckboxId = useId()

  // Calculate final amount including processing fee if selected
  const getFinalAmount = (): number | null => {
    if (!amount) return null
    return coverProcessingFee ? calculateAmountWithFee(amount) : amount
  }

  // Return URL after embedded Checkout (one-time or monthly)
  const getReturnUrl = () => {
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('payment-success', 'true')
    return currentUrl.toString()
  }

  const handleContinueToPayment = async () => {
    const finalAmount = getFinalAmount()
    if (!finalAmount || finalAmount < MINIMUM_DONATION_AMOUNT) {
      setError('Please select a valid donation amount')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const endpoint = '/api/stripe/handle-donation'
      const returnUrl = getReturnUrl()

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: finalAmount,
          currency: 'usd',
          coverProcessingFee,
          isMonthly,
          returnUrl,
          ...(note.trim() !== '' && { note: note.trim() }),
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to start checkout')
      }

      const data = await response.json()
      setClientSecret(data.clientSecret)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize payment')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full font-family-body">
      <label
        htmlFor={monthlyCheckboxId}
        className={cn(
          'mb-6 block w-full cursor-pointer rounded-xl border border-border bg-white p-1 font-body',
          'focus-within:ring-2 focus-within:ring-evergreen-500 focus-within:ring-offset-0 focus-within:outline-none',
        )}
      >
        <input
          id={monthlyCheckboxId}
          type="checkbox"
          checked={isMonthly}
          onChange={(e) => setIsMonthly(e.target.checked)}
          className="sr-only"
          aria-label="Donation frequency: One-Time or Monthly"
        />
        <span className="relative flex h-13 w-full gap-1.5">
          <div
            className={cn(
              'absolute inset-y-0 z-1 w-[50%] rounded-lg bg-evergreen-800 transition-transform',
              isMonthly && 'translate-x-full',
            )}
          ></div>
          <span
            className={cn(
              'relative z-2 flex flex-1 items-center justify-center rounded-lg px-4 text-center text-base font-medium text-foreground transition-colors',
              !isMonthly && 'text-white',
            )}
          >
            One-Time
          </span>
          <span
            className={cn(
              'relative z-2 flex flex-1 items-center justify-center rounded-lg px-4 text-center text-base font-medium text-foreground transition-colors',
              isMonthly && 'text-white',
            )}
          >
            Monthly
          </span>
        </span>
      </label>
      <PaymentAmountGrid
        values={donationAmounts}
        suggestedDonation={suggestedDonation}
        amount={amount}
        onAmountChange={setAmount}
        note={note}
        onNoteChange={setNote}
      />
      <div className="mt-6 space-y-3">
        <label className="flex cursor-pointer items-center gap-3">
          <Checkbox
            checked={coverProcessingFee}
            onCheckedChange={(checked) => setCoverProcessingFee(checked === true)}
          />
          <span className="text-sm text-card-foreground">
            I&apos;ll cover the processing fee – add it to my donation.
            {coverProcessingFee && amount && (
              <span className="ml-1 text-secondary-foreground">
                (+${(calculateAmountWithFee(amount) - amount).toFixed(2)})
              </span>
            )}
          </span>
        </label>
      </div>
      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
      <Button
        onClick={handleContinueToPayment}
        disabled={!amount || amount < MINIMUM_DONATION_AMOUNT || isLoading}
        className="mt-6 h-15 w-full font-body text-base [&_span.size-4]:size-6"
        variant="accent"
        customIcon={<GiftIcon className="size-4" />}
      >
        {isLoading
          ? 'Loading...'
          : !amount
            ? 'Donate'
            : isMonthly
              ? `Donate ${formatAmount(getFinalAmount() ?? amount)}/month`
              : `Donate ${formatAmount(getFinalAmount() ?? amount)}`}
      </Button>
      <div className="mt-4 flex items-center justify-center">
        <LockIcon className="mr-1.5 size-3.5 text-secondary-foreground" />
        <p className="text-center text-[13px] text-muted-foreground">
          Secure payment powered by Stripe
        </p>
      </div>
      <p className="mt-4 border-t border-border pt-6 text-center text-sm text-muted-foreground">
        This donation is made to MAHA Action, Inc. for general support of its programs. Please note
        that contributions to a 501(c)(4) organization are not tax-deductible under federal law.
      </p>
    </div>
  )
}
